import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, Phone, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotLoginId() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!value.trim()) {
      toast.error("Enter your email or mobile number");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-login-id`, {
        email_or_mobile: value.trim(),
      });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to look up account");
    } finally {
      setLoading(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    setCopied(txt);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div
      className="min-h-[100dvh] bg-[#07111F] text-white flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="px-6 md:px-10 pt-8">
        <button
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"
          data-testid="forgot-loginid-back"
        >
          <ArrowLeft size={14} />
          Back to login
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-xs uppercase tracking-[0.22em] text-white/40 mb-3">
            Account recovery
          </div>
          <h1 className="text-3xl md:text-4xl font-light leading-tight">
            Forgot your <span className="italic text-[#C8A96B]">login ID?</span>
          </h1>
          <p className="mt-4 text-sm text-white/60 leading-relaxed">
            Enter the email address or mobile number registered with your
            account. We'll show you the login ID(s) you can use.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-white/45 mb-2">
                Email or mobile
              </label>
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="you@example.com  or  9876543210"
                data-testid="forgot-loginid-input"
                className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#C8A96B]/60 transition"
                autoFocus
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="forgot-loginid-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-[#C8A96B] text-[#07111F] font-medium hover:bg-[#d4b878] transition disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              Show my login ID
            </button>
          </form>

          {result && (
            <div
              className="mt-8 border border-white/10 rounded-lg p-5 bg-white/[0.03]"
              data-testid="forgot-loginid-result"
            >
              <p className="text-sm text-white/65 leading-relaxed">
                {result.message}
              </p>
              {result.options && result.options.length > 0 ? (
                <ul className="mt-4 space-y-2">
                  {result.options.map((o, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-3 bg-[#07111F] border border-white/10 rounded px-3 py-2"
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#C8A96B]/80">
                          {o.kind}
                        </div>
                        <div className="text-sm font-mono text-white mt-0.5">
                          {o.login_id}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => copy(o.login_id)}
                        className="p-1.5 rounded hover:bg-white/5 transition text-white/55 hover:text-white"
                        title="Copy"
                      >
                        {copied === o.login_id ? (
                          <Check size={14} className="text-emerald-400" />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-4 text-xs text-white/45 italic">
                  No matching account found. Double-check the email or mobile
                  you used while registering.
                </p>
              )}
              <div className="mt-5 flex items-center gap-4 text-xs text-white/45">
                <Link
                  to="/forgot-password"
                  className="hover:text-[#C8A96B] transition"
                >
                  Forgot password?
                </Link>
                <span className="text-white/15">·</span>
                <Link to="/login" className="hover:text-[#C8A96B] transition">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-3 text-[11px] text-white/40">
            <div className="flex items-center gap-2">
              <Mail size={12} />
              Email lookup
            </div>
            <div className="flex items-center gap-2">
              <Phone size={12} />
              Mobile lookup
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
